import React from "react";
import { Button, Drawer, Stack, Typography } from "@mui/material";
import { TOUCH_TARGET } from "../../theme/theme";
import type { ContentReviewItem } from "../../services/contentReviewService";

/**
 * SLICE B — the ✗ confirm step. Grant's ruling, verbatim:
 *   "confirm step - dont want to lose one by accident - lets go"
 *
 * WHY IT EXISTS: a rejected package lands in `parked`, and `parked` is absent
 * from VERDICT_ELIGIBLE_STATUSES (awaiting_review · revised · qa_failed). So a
 * mis-tapped ✗ is UNRECOVERABLE from this UI — it can never be approved and
 * never be sent back for a re-render. It is also about to be the most-pressed
 * control on the page, because none of the current queue is approval-ready.
 * The copy therefore states the FINALITY rather than merely asking "are you
 * sure": the point is that he can see at a glance this one does not come back.
 *
 * 🔴 THIS COMPONENT IS DELIBERATELY INCAPABLE OF CARRYING WORDS, AND THAT IS
 *    ITS WHOLE DESIGN — NOT AN OVERSIGHT TO BE HELPFULLY CORRECTED LATER.
 *
 *    `H` is { operations: [], max_attempts: 0, stop_code: "R-HOLD" }
 *    (revisionEngine.ts:37) — ZERO OPERATIONS, exactly like `P`. The engine
 *    runs nothing on it, so any words attached to it are discarded silently.
 *    That is precisely the defect that ate Grant's real correction on
 *    2026-07-25, when he approved a reel with the note "Text on screen needs
 *    work" and `P` threw the note away.
 *
 *    A confirmation sheet is exactly the affordance a later edit would grow a
 *    "say why" box onto — it already has a title, a body and a button, so a
 *    field looks like it belongs. The refusal is therefore STRUCTURAL, not a
 *    comment asking nicely:
 *      · this component takes NO note prop and holds NO state;
 *      · `onConfirm` is `(item) => void` — no parameter words could be passed
 *        through, so a field added here would have nowhere to send them;
 *      · it imports no text-input primitive at all.
 *    `backend/tests/test_content_verdict_hold_migration.py::
 *    test_the_reject_confirm_cannot_carry_words` fails if any of that changes,
 *    and a jsdom test fails if a textbox ever appears on this surface.
 *
 *    ⇒ If a reviewer's words are ever wanted on a rejection, they cannot be
 *      bolted on here. The verdict would first have to stop being terminal, or
 *      the words would need a home the engine actually reads. Either is a new
 *      packet with its own ruling — not an edit to this file.
 */
interface Props {
  /** The package awaiting confirmation, or null when the sheet is closed. */
  item: ContentReviewItem | null;
  busy: boolean;
  /** Yes. Takes the item and nothing else — no note parameter, by design. */
  onConfirm: (item: ContentReviewItem) => void;
  /** No. */
  onCancel: () => void;
}

const SHEET_PAPER = {
  maxWidth: 560,
  mx: "auto",
  borderRadius: "24px 24px 0 0",
  p: { xs: 2, sm: 3 },
  pb: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
} as const;

const ContentRejectConfirm: React.FC<Props> = ({ item, busy, onConfirm, onCancel }) => (
  <Drawer
    anchor="bottom"
    open={Boolean(item)}
    onClose={() => !busy && onCancel()}
    PaperProps={{ sx: SHEET_PAPER }}
  >
    <Typography variant="h5" fontWeight={800}>
      Reject this one for good?
    </Typography>
    <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
      <strong>{item?.subject}</strong> moves to Rejected and stays there. It cannot
      be approved later, and it cannot be sent back for a re-render — this is the
      end of the line for it. Nothing is deleted: it is kept as one of the bad
      ones to learn from.
    </Typography>
    <Stack spacing={1}>
      <Button
        variant="contained"
        color="error"
        aria-label="Confirm reject"
        disabled={busy}
        onClick={() => item && onConfirm(item)}
        sx={{ minHeight: TOUCH_TARGET }}
      >
        Yes, reject it
      </Button>
      <Button
        aria-label="Cancel reject"
        disabled={busy}
        onClick={onCancel}
        sx={{ minHeight: TOUCH_TARGET }}
      >
        Keep it in Review
      </Button>
    </Stack>
  </Drawer>
);

export default ContentRejectConfirm;
